import React, { useEffect, useRef, useState } from 'react';
import { Ban } from 'lucide-react';
import { cn } from '../../lib/cn';
import { LeadColor, LEAD_COLORS, LEAD_COLOR_META } from '../../types/leads.types';

interface LeadColorPickerProps {
    value: LeadColor | null;
    onChange: (color: LeadColor | null) => void;
    className?: string;
}

/**
 * Pallino cliccabile sulla card del lead: apre la tavolozza dei 6 colori e
 * applica l'etichetta scelta (o la rimuove). Blocca la propagazione degli
 * eventi perché la card è cliccabile e trascinabile (kanban).
 */
export function LeadColorPicker({ value, onChange, className }: LeadColorPickerProps) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const current = value ? LEAD_COLOR_META[value] : null;

    useEffect(() => {
        if (!open) return;

        const handlePointerDown = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown as unknown as EventListener);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown as unknown as EventListener);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    const pick = (color: LeadColor | null) => {
        setOpen(false);
        if (color !== value) onChange(color);
    };

    return (
        <div
            ref={rootRef}
            className={cn('relative', className)}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                title={current ? `Colore: ${current.label}` : 'Assegna un colore'}
                aria-label={current ? `Colore: ${current.label}` : 'Assegna un colore'}
                className={cn(
                    "relative w-4 h-4 rounded-full border transition-transform hover:scale-110",
                    "after:absolute after:-inset-2.5 after:content-[''] md:after:hidden",
                    current ? cn(current.dotClass, 'border-transparent') : 'bg-white border-gray-300 border-dashed'
                )}
            />

            {open && (
                <div className="absolute right-0 bottom-full mb-1.5 z-30 flex items-center gap-1.5 rounded-lg border bg-white p-1.5 shadow-lg">
                    {LEAD_COLORS.map((c) => (
                        <button
                            key={c.key}
                            type="button"
                            onClick={() => pick(c.key)}
                            title={c.label}
                            aria-label={c.label}
                            className={cn(
                                'w-7 h-7 md:w-5 md:h-5 rounded-full transition-transform hover:scale-110',
                                c.dotClass,
                                value === c.key && 'ring-2 ring-offset-1 ring-gray-500'
                            )}
                        />
                    ))}
                    <button
                        type="button"
                        onClick={() => pick(null)}
                        title="Nessun colore"
                        aria-label="Nessun colore"
                        className="w-7 h-7 md:w-5 md:h-5 rounded-full border border-gray-300 text-gray-400 flex items-center justify-center hover:bg-gray-50"
                    >
                        <Ban className="w-3 h-3" />
                    </button>
                </div>
            )}
        </div>
    );
}

export default LeadColorPicker;
